// src/hooks/useVoiceRecorder.js
import { useState, useRef, useCallback } from 'react';

const SUPPORTED_MIME_TYPES = [
  'audio/webm;codecs=opus',
  'audio/webm',
  'audio/ogg;codecs=opus',
  'audio/ogg',
  'audio/mp4',
];

const getSupportedMimeType = () =>
  SUPPORTED_MIME_TYPES.find(t => MediaRecorder.isTypeSupported(t)) || '';

export const useVoiceRecorder = ({
  onTranscriptionComplete,
  onError,
  maxDurationMs = 120000
}) => {
  const [isRecording,     setIsRecording]     = useState(false);
  const [isPaused,        setIsPaused]        = useState(false);
  const [isTranscribing,  setIsTranscribing]  = useState(false);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const [audioLevel,      setAudioLevel]      = useState(0);
  const [error,           setError]           = useState(null);
  const [transcribeProgress, setTranscribeProgress] = useState(0); // 0-100

  const mediaRecorderRef    = useRef(null);
  const audioChunksRef      = useRef([]);
  const streamRef           = useRef(null);
  const timerRef            = useRef(null);
  const maxDurationTimerRef = useRef(null);
  const analyserRef         = useRef(null);
  const animationFrameRef   = useRef(null);
  const audioContextRef     = useRef(null);

  // ── Audio level monitoring ─────────────────────────────────────────────
  const startAudioLevelMonitoring = useCallback((stream) => {
    try {
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      analyserRef.current = audioContextRef.current.createAnalyser();
      analyserRef.current.fftSize = 256;

      const source = audioContextRef.current.createMediaStreamSource(stream);
      source.connect(analyserRef.current);
      const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount);

      const update = () => {
        if (!analyserRef.current) return;
        analyserRef.current.getByteFrequencyData(dataArray);
        const avg = dataArray.reduce((a, b) => a + b, 0) / dataArray.length;
        setAudioLevel(Math.min(100, (avg / 128) * 100));
        animationFrameRef.current = requestAnimationFrame(update);
      };
      update();
    } catch (err) {
      console.warn('Audio level monitoring unavailable:', err);
    }
  }, []);

  const stopAudioLevelMonitoring = useCallback(() => {
    if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);
    if (audioContextRef.current) {
      audioContextRef.current.close().catch(() => {});
      audioContextRef.current = null;
    }
    analyserRef.current = null;
    setAudioLevel(0);
  }, []);

  // ── Start recording ────────────────────────────────────────────────────
  const startRecording = useCallback(async () => {
    try {
      setError(null);
      setTranscribeProgress(0);
      audioChunksRef.current = [];

      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          sampleRate:       16000,
          channelCount:     1
        }
      });

      streamRef.current = stream;

      const mimeType     = getSupportedMimeType();
      const mediaRecorder = new MediaRecorder(stream, mimeType ? { mimeType } : {});
      mediaRecorderRef.current = mediaRecorder;

      mediaRecorder.ondataavailable = (e) => {
        if (e.data?.size > 0) audioChunksRef.current.push(e.data);
      };

      mediaRecorder.onerror = () => {
        setError('Recording error occurred');
        stopRecording();
      };

      mediaRecorder.start(250);
      setIsRecording(true);
      setIsPaused(false);
      setRecordingDuration(0);

      timerRef.current = setInterval(() => {
        setRecordingDuration(prev => prev + 1);
      }, 1000);

      maxDurationTimerRef.current = setTimeout(stopRecording, maxDurationMs);

      startAudioLevelMonitoring(stream);

    } catch (err) {
      const msg =
        err.name === 'NotAllowedError' ? 'Microphone permission denied.'
        : err.name === 'NotFoundError'  ? 'No microphone found.'
        : err.name === 'NotSupportedError' ? 'Recording not supported in this browser.'
        : 'Could not start recording.';
      setError(msg);
      onError?.(msg);
    }
  }, [maxDurationMs, startAudioLevelMonitoring, onError]);

  // ── Stop recording (returns blob) ──────────────────────────────────────
  const stopRecording = useCallback(() => {
    return new Promise((resolve) => {
      if (!mediaRecorderRef.current ||
          mediaRecorderRef.current.state === 'inactive') {
        resolve(null);
        return;
      }

      if (timerRef.current)            clearInterval(timerRef.current);
      if (maxDurationTimerRef.current) clearTimeout(maxDurationTimerRef.current);

      stopAudioLevelMonitoring();

      mediaRecorderRef.current.onstop = () => {
        const mimeType  = mediaRecorderRef.current?.mimeType || 'audio/webm';
        const audioBlob = new Blob(audioChunksRef.current, { type: mimeType });

        if (streamRef.current) {
          streamRef.current.getTracks().forEach(t => t.stop());
          streamRef.current = null;
        }

        setIsRecording(false);
        setIsPaused(false);
        resolve(audioBlob);
      };

      mediaRecorderRef.current.stop();
    });
  }, [stopAudioLevelMonitoring]);

  // ── Stop + transcribe ──────────────────────────────────────────────────
  const stopAndTranscribe = useCallback(async (transcribeFn) => {
    const audioBlob = await stopRecording();

    if (!audioBlob || audioBlob.size < 1000) {
      setError('Recording too short. Please try again.');
      return;
    }

    setIsTranscribing(true);
    setTranscribeProgress(0);
    setError(null);

    try {
      const transcription = await transcribeFn(
        audioBlob,
        (progress, status) => {
          setTranscribeProgress(progress);
          console.log(`Transcription progress: ${progress}% (${status})`);
        }
      );
      onTranscriptionComplete?.(transcription);
    } catch (err) {
      const msg = err.message || 'Transcription failed. Please try again.';
      setError(msg);
      onError?.(msg);
    } finally {
      setIsTranscribing(false);
      setTranscribeProgress(0);
      setRecordingDuration(0);
    }
  }, [stopRecording, onTranscriptionComplete, onError]);

  // ── Pause / Resume ─────────────────────────────────────────────────────
  const pauseRecording = useCallback(() => {
    if (mediaRecorderRef.current?.state === 'recording') {
      mediaRecorderRef.current.pause();
      setIsPaused(true);
      if (timerRef.current) clearInterval(timerRef.current);
    }
  }, []);

  const resumeRecording = useCallback(() => {
    if (mediaRecorderRef.current?.state === 'paused') {
      mediaRecorderRef.current.resume();
      setIsPaused(false);
      timerRef.current = setInterval(() => {
        setRecordingDuration(prev => prev + 1);
      }, 1000);
    }
  }, []);

  // ── Cancel ─────────────────────────────────────────────────────────────
  const cancelRecording = useCallback(() => {
    if (timerRef.current)            clearInterval(timerRef.current);
    if (maxDurationTimerRef.current) clearTimeout(maxDurationTimerRef.current);

    stopAudioLevelMonitoring();

    if (mediaRecorderRef.current &&
        mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.onstop = null;
      mediaRecorderRef.current.stop();
    }

    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }

    audioChunksRef.current = [];
    setIsRecording(false);
    setIsPaused(false);
    setIsTranscribing(false);
    setRecordingDuration(0);
    setTranscribeProgress(0);
    setError(null);
  }, [stopAudioLevelMonitoring]);

  const formatDuration = (s) =>
    `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, '0')}`;

  return {
    isRecording,
    isPaused,
    isTranscribing,
    transcribeProgress,
    recordingDuration,
    formattedDuration: formatDuration(recordingDuration),
    audioLevel,
    error,
    startRecording,
    stopRecording,
    stopAndTranscribe,
    pauseRecording,
    resumeRecording,
    cancelRecording,
    isSupported: !!(navigator.mediaDevices?.getUserMedia && window.MediaRecorder)
  };
};
